import { ArrowRight, BookOpen, CalendarClock } from "lucide-react";
import { useTranslation } from "react-i18next";
import { CtaAnchor } from "@/src/common/components/cta-link";
import { EyebrowBadge } from "@/src/common/components/eyebrow-badge";
import { PageHelmet } from "@/src/common/components/page-helmet";
import { Reveal } from "@/src/common/components/reveal";
import { SectionHeading } from "@/src/common/components/section-heading";
import { env } from "@/src/common/constants/env";
import { ContactForm } from "@/src/contact/components/contact-form";
import { ContactInfo } from "@/src/contact/components/contact-info";

export function ContactPage() {
	const { t } = useTranslation("contact");

	return (
		<>
			<PageHelmet title={t("meta.title")} description={t("meta.description")} />

			<section className="bg-mesh relative isolate overflow-hidden pt-20 pb-12 md:pt-32 md:pb-16">
				<div className="mx-auto max-w-3xl px-4 text-center sm:px-6 lg:px-8">
					<SectionHeading
						as="h1"
						eyebrow={<EyebrowBadge tone="ink">{t("hero.eyebrow")}</EyebrowBadge>}
						title={t("hero.title")}
						titleAccent={t("hero.title_accent")}
						description={t("hero.subtitle")}
					/>
				</div>
			</section>

			<section className="pt-12 pb-32 md:pt-16">
				<div className="mx-auto grid max-w-6xl gap-8 px-4 sm:px-6 lg:grid-cols-[1.4fr_1fr] lg:px-8">
					<Reveal className="rounded-3xl border border-default-200 bg-white p-8 shadow-card md:p-10">
						<ContactForm />
					</Reveal>

					<div className="flex flex-col gap-5">
						<Reveal delay={0.1}>
							<ContactInfo />
						</Reveal>

						<Reveal delay={0.18}>
							<a
								href={env.CALENDLY_URL}
								target="_blank"
								rel="noreferrer"
								className="group flex items-start gap-3 rounded-3xl border border-default-200 bg-white p-6 shadow-card transition hover:border-brand-300 hover:shadow-lift"
							>
								<span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
									<CalendarClock className="h-5 w-5" />
								</span>
								<div className="min-w-0 flex-1">
									<p className="text-base font-semibold text-foreground">{t("quick.demo.title")}</p>
									<p className="mt-1 text-sm text-default-600">{t("quick.demo.body")}</p>
									<span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-700 transition-colors group-hover:text-brand-800">
										{t("quick.demo.cta")}
										<ArrowRight className="h-3.5 w-3.5" />
									</span>
								</div>
							</a>
						</Reveal>

						<Reveal delay={0.26}>
							<CtaAnchor
								href="https://docs.upsquad.com"
								target="_blank"
								rel="noreferrer"
								variant="outline"
								size="md"
								className="justify-start gap-3 rounded-3xl border-default-200 bg-white p-6 shadow-card hover:border-brand-300 hover:shadow-lift"
							>
								<span className="inline-flex size-10 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-700">
									<BookOpen className="h-5 w-5" />
								</span>
								<div className="min-w-0 flex-1 text-left">
									<p className="text-base font-semibold text-foreground">{t("quick.help.title")}</p>
									<p className="mt-1 text-sm font-normal text-default-600">{t("quick.help.body")}</p>
								</div>
								<ArrowRight className="h-4 w-4 shrink-0 text-default-500" />
							</CtaAnchor>
						</Reveal>
					</div>
				</div>
			</section>
		</>
	);
}
